import React, { useMemo, useState } from "react";

import qs from "query-string";
import dayjs from "dayjs";
import { useQuery } from "@apollo/client";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import DatePicker from "react-datepicker";

import {
  QUERY_SYNGUARD_GENERIC_LABELS,
  QUERY_BUILDINGS_FOR_ACCESS_LABELS,
} from "../../config/graphql/query";

import Pagination from "../../components/Pagination";
import { useClientId } from "../Clients/Client/hooks/useClient";
import { reactSelectCustomStyles } from "../Employees/Employee/Information";

const limit = 10;

type LabelStatus = "Active" | "Expired";

type SelectOption = { value: string; label: string };

const StatusBadge: React.FC<{ status: LabelStatus }> = ({ status }) => {
  const { t } = useTranslation(["accessLabels"]);

  const variantMap: Record<LabelStatus, string> = {
    Active: "success",
    Expired: "secondary",
  };

  return (
    <Badge variant={variantMap[status]}>
      {t(`accessLabels:status.${status.toLowerCase()}`)}
    </Badge>
  );
};

const History: React.FC = () => {
  const { t } = useTranslation(["accessLabels", "common"]);

  const clientId = useClientId();
  const location = useLocation();
  const history = useHistory();

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        page?: number;
        location?: string;
        day?: string;
      },
    [location.search],
  );

  const page = useMemo(() => Math.max(query.page || 0, 1), [query.page]);

  const [activeOnDate, setActiveOnDate] = useState<Date>(
    query.day ? dayjs(query.day).toDate() : new Date(),
  );

  const updateQuery = (patch: Record<string, string | number | undefined>) => {
    const next = { ...query, ...patch, page: 1 };

    Object.keys(next).forEach((key) => {
      const value = (next as Record<string, unknown>)[key];
      if (value === "" || value === undefined || value === null) {
        delete (next as Record<string, unknown>)[key];
      }
    });

    history.push({ search: qs.stringify(next) });
  };

  useDebounce(
    () => {
      const next = { ...query, page: 1 } as Record<string, unknown>;

      next.day = dayjs(activeOnDate).format("YYYY-MM-DD");

      Object.keys(next).forEach((key) => {
        const value = next[key];
        if (value === "" || value === undefined || value === null) {
          delete next[key];
        }
      });
      history.push({ search: qs.stringify(next) });
    },
    500,
    [activeOnDate],
  );

  const { data: buildingsData } = useQuery<{ buildings: IBuilding[] }>(
    QUERY_BUILDINGS_FOR_ACCESS_LABELS,
    {
      context: { headers: { ClientId: clientId } },
      fetchPolicy: "cache-first",
    },
  );

  const locationOptions = useMemo<SelectOption[]>(
    () =>
      (buildingsData?.buildings ?? []).map((b) => ({
        value: b.id,
        label: b.title,
      })),
    [buildingsData],
  );

  const filter = useMemo(
    () => ({
      ...(query.location ? { entity: query.location } : {}),
      activeOn: dayjs(activeOnDate)
        .hour(12)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toISOString(),
    }),
    [query.location, activeOnDate],
  );

  const { data } = useQuery<{
    synguardGenericLabels: ISynguardGenericLabel[];
    synguardGenericLabelsCount: number;
  }>(QUERY_SYNGUARD_GENERIC_LABELS, {
    variables: {
      filter,
      pagination: { limit, skip: (page - 1) * limit },
    },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const labels = data?.synguardGenericLabels ?? [];
  const totalCount = data?.synguardGenericLabelsCount ?? 0;

  const goToPrevDay = () =>
    setActiveOnDate(dayjs(activeOnDate).subtract(1, "day").toDate());
  const goToNextDay = () =>
    setActiveOnDate(dayjs(activeOnDate).add(1, "day").toDate());

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 mb-2">
        <h6 className="mb-0 mr-3">{t("accessLabels:history.title")}</h6>
        <form className="form-inline ml-auto">
          <div className="d-flex flex-wrap">
            <div className="d-flex mb-2 mr-3 align-items-center">
              <div className="mr-2 text-nowrap">
                {t("accessLabels:filter.activeOn")}
              </div>
              <Button
                variant="outline-secondary"
                onClick={goToPrevDay}
                aria-label={t("accessLabels:filter.previousDay")}
                className="mr-1"
                style={{
                  height: "calc(1.5em + 0.75rem + 2px)",
                  borderColor: "#ced4da",
                }}
              >
                ‹
              </Button>
              <DatePicker
                selected={activeOnDate}
                onChange={(date: Date) => date && setActiveOnDate(date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                todayButton={t("common:today")}
              />
              <Button
                variant="outline-secondary"
                onClick={goToNextDay}
                aria-label={t("accessLabels:filter.nextDay")}
                className="ml-1"
                style={{
                  height: "calc(1.5em + 0.75rem + 2px)",
                  borderColor: "#ced4da",
                }}
              >
                ›
              </Button>
            </div>
            <div className="d-flex mb-2">
              <label
                htmlFor="al-location"
                className="mb-0 mr-2 align-self-center text-nowrap"
              >
                {t("accessLabels:filter.location")}
              </label>
              <div style={{ width: "13rem" }}>
                <Select<SelectOption>
                  inputId="al-location"
                  isClearable
                  options={locationOptions}
                  value={
                    locationOptions.find((o) => o.value === query.location) ??
                    null
                  }
                  onChange={(option) =>
                    updateQuery({ location: option?.value })
                  }
                  styles={reactSelectCustomStyles(false)}
                />
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>{t("accessLabels:table.th.code")}</th>
                <th>{t("accessLabels:table.th.createdAt")}</th>
                <th>{t("accessLabels:table.th.createdBy")}</th>
                <th>{t("accessLabels:table.th.location")}</th>
                <th>{t("accessLabels:table.th.validFrom")}</th>
                <th>{t("accessLabels:table.th.validUntil")}</th>
                <th>{t("accessLabels:table.th.status")}</th>
              </tr>
            </thead>
            <tbody>
              {labels.map((label) => (
                <tr key={label.id}>
                  <td>
                    <code>#{label.code.slice(-6).toUpperCase()}</code>
                  </td>
                  <td>{dayjs(label.createdAt).format("DD.MM.YYYY HH:mm")}</td>
                  <td>
                    {[label.createdBy?.firstName, label.createdBy?.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </td>
                  <td>{label.entity?.title ?? "—"}</td>
                  <td>{dayjs(label.validFrom).format("DD.MM.YYYY")}</td>
                  <td>{dayjs(label.validUntil).format("DD.MM.YYYY")}</td>
                  <td>
                    <StatusBadge status={label.status} />
                  </td>
                </tr>
              ))}
              {labels.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    {t("accessLabels:table.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3">
        <Pagination documentsCount={totalCount} limit={limit} />
      </div>
    </>
  );
};

export default History;
